import { useEffect, useMemo, useState } from "react";
import MSINav from "./MSINav";
import { useEntitlements } from "./entitlements/EntitlementContext";
import { fetchMsiAdminRelease } from "./msi/adminRelease";

const panel = {
  background: "#171717",
  border: "1px solid #2A2A2A",
  borderRadius: "12px",
  padding: "18px",
};

const muted = {
  color: "#A0A0A0",
};

const metricNumber = {
  fontSize: 42,
  fontWeight: 900,
  lineHeight: 1,
  marginTop: 8,
};

const barColor = (score, bias) => {
  if (bias === "PUT") return "#ef4444";
  if (bias === "CALL") return "#22c55e";
  if (score >= 70) return "#22c55e";
  if (score >= 45) return "#a3a3a3";
  return "#ef4444";
};

function readLocalArray(key) {
  try {
    const value = JSON.parse(localStorage.getItem(key) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function ProgressBar({ value = 0, bias }) {
  const safe = Math.max(0, Math.min(100, Number(value) || 0));

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div
        style={{
          width: 220,
          height: 18,
          background: "#2A2A2A",
          borderRadius: 4,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${safe}%`,
            height: "100%",
            background: barColor(safe, bias),
          }}
        />
      </div>
      <span>{Math.round(safe)}</span>
    </div>
  );
}

function MetricCard({ label, value, color = "white" }) {
  return (
    <div style={panel}>
      <div style={{ ...muted, fontWeight: 800 }}>{label}</div>
      <div style={{ ...metricNumber, color }}>{value}</div>
    </div>
  );
}

function fmtDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;

  return d.toLocaleString([], {
    year: "2-digit",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function actionColor(action) {
  const a = String(action || "").toUpperCase();
  if (a.includes("EXIT") || a.includes("CUT")) return "#ef4444";
  if (a.includes("BUY") || a.includes("ENTER")) return "#22c55e";
  if (a.includes("TRAIL") || a.includes("PROTECT")) return "#f59e0b";
  return "#A0A0A0";
}

export default function MSIDashboard() {
  const entitlement = useEntitlements();
  const [brief, setBrief] = useState(null);
  const [signals, setSignals] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [workflow, setWorkflow] = useState({
    watchlist: [],
    alerts: [],
    positions: [],
    closed: [],
  });

  useEffect(() => {
    if (!entitlement.isAdmin) return undefined;
    let active = true;
    const request = fetchMsiAdminRelease().then(({ contract }) => {
      const environment = contract.market_environment || {};
      const structures = Array.isArray(contract.structures) ? contract.structures : [];
      return [{
        market_health_score: environment.health_score,
        market_health_label: environment.risk_state,
        vix_regime: environment.vix_regime,
        vix_state: environment.vix_state,
        top_calls: structures.filter((row) => row.direction === "UP"),
        top_puts: structures.filter((row) => row.direction === "DOWN"),
        top_3d_recoveries: [],
        sector_strength: [],
      }, structures];
    });
    request
      .then(([briefData, signalData]) => {
        if (!active) return;
        setBrief(briefData);
        setSignals(signalData);
        setLoadFailed(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("MSI operator data unavailable", err?.message || "UNKNOWN");
        setLoadFailed(true);
      });
    return () => { active = false; };
  }, [entitlement.isAdmin]);

  useEffect(() => {
    function loadWorkflow() {
      setWorkflow({
        watchlist: readLocalArray("msi_watchlist"),
        alerts: readLocalArray("msi_alerts"),
        positions: readLocalArray("msi_open_positions"),
        closed: readLocalArray("msi_closed_positions"),
      });
    }

    if (!entitlement.isAdmin) return undefined;
    loadWorkflow();

    window.addEventListener("storage", loadWorkflow);
    window.addEventListener("focus", loadWorkflow);

    return () => {
      window.removeEventListener("storage", loadWorkflow);
      window.removeEventListener("focus", loadWorkflow);
    };
  }, [entitlement.isAdmin]);

  const activeAlerts = useMemo(
    () => workflow.alerts.filter((a) => !a.archived),
    [workflow.alerts]
  );

  const recentAlerts = useMemo(
    () =>
      [...workflow.alerts]
        .filter((a) => !a.archived)
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .slice(0, 5),
    [workflow.alerts]
  );

  if (loadFailed) {
    return (
      <div style={{ minHeight: "100vh", background: "#0B0B0B", color: "white", padding: 24 }}>
        Market intelligence is being refreshed. Current structures are temporarily unavailable.
      </div>
    );
  }

  if (!brief) {
    return (
      <div style={{ minHeight: "100vh", background: "#0B0B0B", color: "white", padding: 24 }}>
        Loading MSI...
      </div>
    );
  }

  const marketBar = `Market Health ${brief.market_health_score ?? "N/A"} | ${brief.market_health_label} | VIX ${brief.vix_regime} | ${brief.vix_state}`;

  const fallbackAlerts = [
    ...(brief.top_calls || []).slice(0, 2).map((x) => `${x.ticker} showing ${x.market_state}`),
    ...(brief.top_puts || []).slice(0, 2).map((x) => `${x.ticker} showing ${x.market_state}`),
    `VIX is ${brief.vix_state}`,
  ];

  const watchlistTickers = workflow.watchlist.length
    ? workflow.watchlist.slice(0, 10).map((p) => p.ticker)
    : ["SPY", "QQQ", "IWM", "DIA", "", "XLF", "XLV", "XLI", "XLK", "XLE"];

  return (
    <div style={{ minHeight: "100vh", background: "#0B0B0B", color: "#FFFFFF", padding: 24 }}>
      <h1 style={{ fontSize: 42, marginBottom: 12 }}>
        MSI | Market Structure Intelligence
      </h1>

      <MSINav />

      <div style={{ ...panel, marginBottom: 18 }}>
        {marketBar}
      </div>

      {entitlement.isAdmin && <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(160px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <MetricCard label="Watchlist" value={workflow.watchlist.length} />
        <MetricCard label="Active Alerts" value={activeAlerts.length} color="#22c55e" />
        <MetricCard label="Open Positions" value={workflow.positions.length} />
        <MetricCard label="Closed Trades" value={workflow.closed.length} color="#A0A0A0" />
      </div>}

      {entitlement.isAdmin && <div
        style={{
          display: "grid",
          gridTemplateColumns: "240px 1fr 420px",
          gap: 20,
        }}
      >
        <div style={panel}>
          <h2>WATCHLIST</h2>

          {watchlistTickers.map((t, i) => (
            <p key={`${t || "blank"}-${i}`} style={{ color: t ? "#E5E5E5" : "transparent" }}>
              {t || "-"}
            </p>
          ))}

          {workflow.watchlist.length === 0 && (
            <p style={{ color: "#A0A0A0", marginTop: 18 }}>
              Add plays from Universe to populate this panel.
            </p>
          )}
        </div>

        <div style={{ display: "grid", gap: 20 }}>
          <div style={panel}>
            <h2>SECTOR CAPITAL FLOW</h2>

            {(brief.sector_strength || []).slice(0, 8).map((s) => (
              <div
                key={s.ticker}
                style={{
                  display: "grid",
                  gridTemplateColumns: "160px 1fr",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <div>
                  {s.sector} <span style={{ color: "#A0A0A0" }}>({s.ticker})</span>
                </div>
                <ProgressBar value={s.weekly_sustainability_score || s.confidence || 0} bias={s.bias} />
              </div>
            ))}
          </div>

          <div style={panel}>
            <h2>RECOVERY BOARD</h2>

            <div style={{ display: "grid", gridTemplateColumns: "80px 190px 80px 100px 1fr", color: "#A0A0A0", marginBottom: 10 }}>
              <div>Ticker</div>
              <div>Stage</div>
              <div>Weekly</div>
              <div>Conf</div>
              <div>Target</div>
            </div>

            {(brief.top_3d_recoveries || []).slice(0, 8).map((x) => (
              <div key={x.ticker} style={{ display: "grid", gridTemplateColumns: "80px 190px 80px 100px 1fr", marginBottom: 12 }}>
                <div>{x.ticker}</div>
                <div>{x.recovery_3d_stage}</div>
                <div>{x.weekly_trend}</div>
                <div>{x.confidence}</div>
                <div style={{ color: "#A0A0A0" }}>{x.target}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "grid", gap: 20 }}>
          <div style={panel}>
            <h2>HIGH CONVICTION</h2>

            {(brief.top_calls || []).slice(0, 5).map((x) => (
              <div key={x.ticker} style={{ display: "grid", gridTemplateColumns: "70px 1fr", alignItems: "center", marginBottom: 14 }}>
                <div>{x.ticker}</div>
                <ProgressBar value={x.confidence} bias="CALL" />
              </div>
            ))}

            {(brief.top_puts || []).slice(0, 5).map((x) => (
              <div key={x.ticker} style={{ display: "grid", gridTemplateColumns: "70px 1fr", alignItems: "center", marginBottom: 14 }}>
                <div>{x.ticker}</div>
                <ProgressBar value={x.confidence} bias="PUT" />
              </div>
            ))}
          </div>

          <div style={panel}>
            <h2>RECENT ALERTS</h2>

            {recentAlerts.length > 0 ? (
              recentAlerts.map((a) => (
                <div
                  key={a.id}
                  style={{
                    borderBottom: "1px solid #242424",
                    padding: "10px 0",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <strong>{a.ticker} {a.contract}</strong>
                    <span style={{ color: actionColor(a.action), fontWeight: 900 }}>
                      {a.action}
                    </span>
                  </div>
                  <div style={{ color: "#A0A0A0", fontSize: 13, marginTop: 4 }}>
                    {a.target || a.thesis || "-"}
                  </div>
                  <div style={{ color: "#737373", fontSize: 12, marginTop: 2 }}>
                    {fmtDate(a.created_at)}
                  </div>
                </div>
              ))
            ) : (
              fallbackAlerts.map((a, i) => (
                <p key={i} style={{ color: "#D4D4D4" }}>
                  • {a}
                </p>
              ))
            )}
          </div>
        </div>
      </div>}
    </div>
  );
}
